import React from 'react';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { businessAdd } from '../../../../../util/business-days';
import { FluentDatePicker } from '../../../../../components/FluentUI/Fluent-DatePicker';
import SearchableDropdown from '../../../../../components/searchable-dropdown/SearchableDropdown';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import PropTypes from 'prop-types';
import { GetProjectMembersAndGroups } from '../../../../../services/api/project-api';
import { markProjectMembership } from '../helpers/task-helpers';

const setEndDate = (setFieldValue, currTaskType) => {
    if (!currTaskType)
        return;
    let duration = currTaskType?.Days;
    let isBusinessDays = currTaskType?.IsBusinessDays;
    let today = new Date();
    if (isBusinessDays) {
        today = businessAdd(new Date(), duration);
    }
    else {
        today.setDate(today.getDate() + duration);
    }

    let pEnd = duration !== 0 ? today : "";
    setFieldValue("Due", pEnd, false);
}

const getDateObj = (values, start) => {
    if (values.selectedProject.length !== 0) {
        return (start ? new Date(values.selectedProject.Start) : new Date(values.selectedProject.End));
    }
}

export const TaskName = ({ values, touched, errors, handleChange, disabled, setTaskName, labels }) => {
    let onNameChange = (e) =>{
        handleChange(e);
        setTaskName(e.target.value);
    }
    return (
        <Form.Group className="task-name">
            <Form.Label>{labels?.TaskSingular ?? 'Task'} Name <span className='red'>*</span></Form.Label>
            <Form.Control
                id="Name"
                name="Name"
                value={values.Name}
                onChange={(e) => onNameChange(e)}
                placeholder={`Enter ${labels?.TaskSingular ?? 'Task'} Name`}
                isInvalid={touched.Name && errors.Name}
                disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">{errors.Name}</Form.Control.Feedback>
        </Form.Group>
    );
}

const truncateProjectName = (project, projectListDropdown, dateFormatID) => {
    const dropdownNodeWidth = 2000;
    let projectPeriod = " [" + new Date(project.Start).toLocaleDateString(dateFormatID) + " to " + new Date(project.End).toLocaleDateString(dateFormatID) + "]";
    let maxChars = (dropdownNodeWidth / 7.5);
    let truncatedName = project.Name.slice(0, maxChars) + (project.Name.length > maxChars ? "..." : "") + projectPeriod;
    return truncatedName;
}

const loadAllUsers = async(projectID, activeProjects, setTaskOtherMembers, setFieldValue, onProjectSelected) => {
    let project = activeProjects.find(project => project.Id === projectID);
    let result = await GetProjectMembersAndGroups(projectID);
    if (result?.status === 200) {
      project.Members = result.data?.ProjectMembers || [];
      project.Groups = result.data?.ProjectGroups || [];
      markProjectMembership(project);

      let DepMem = result.data.DepMembers.filter(
        (user) => !project.Members.some((member) => member?.User?.Id === user.Id)
      ).map((user) => {
        user.User = { ...user };
        return user;
      });
      let DepGroups = result.data.DepGroups.filter(
        (group) => !project.Groups.some((member) => group.Id === member.GroupId)
      ).map((group) => {
        group.IsGroup = true;
        group.User = { ...group };
        return group;
      });
      setFieldValue("selectedProject", project);
      setTaskOtherMembers([
        ...project.Members,
        ...project.Groups,
        ...DepGroups,
        ...DepMem,
      ]);
      if (onProjectSelected) {
        onProjectSelected(project.IsMultiOwner);
      }
    }   
}

export const ProjectName = ({ values, touched, errors, activeProjects, dateFormatID, setTaskOtherMembers, setFieldValue, disabled, labels, onProjectSelected }) => {
    const projectListDropdown = React.createRef();
    const dropdownProjects = [
        ...activeProjects,
        ...(values?.Project && !activeProjects?.some(project => project?.Id === values?.Project?.Id)
            ? [values.Project]
            : [])
    ];
    return (
        <Form.Group className="project-name-control">
            <Form.Label>{labels?.ProjectSingular ?? 'Project'} Name <span className='red'>*</span></Form.Label>
            <div className='project-dropdown-container-create-edit'>
                <div ref={projectListDropdown} style={{ width: '100%', 'margin-right': '2%' }}>
                    <SearchableDropdown
                        values={dropdownProjects?.map(project => {
                            return { id: project.Id, name: truncateProjectName(project, projectListDropdown, dateFormatID) }
                        })}
                        dropdownButtonText={`Search and select a ${labels?.ProjectSingular ?? 'Project'}`}
                        onSelect={(selectedValue) => {
                            values.ProjectId = selectedValue.id;
                            loadAllUsers(selectedValue.id, activeProjects, setTaskOtherMembers, setFieldValue, onProjectSelected);
                        }}
                        currentSelected={{ id: parseInt(values.ProjectId) }}
                        isSearchable={true}
                        validationErrors={touched.ProjectId && errors.ProjectId}
                        disabled={disabled}
                    />
                </div>

                <Link to="/projects/create">
                    <OverlayTrigger placement="bottom-end" trigger={['hover', 'focus']} overlay={
                        <Tooltip className="project-tooltip">
                            Create {labels?.ProjectSingular ?? 'Project'}
                        </Tooltip>
                    }>
                        <Button disabled={disabled} className="project-btn-plus"></Button>
                    </OverlayTrigger>
                </Link>
            </div>
        </Form.Group>
    );
}

export const TaskType = ({ values, touched, errors, handleChange, setFieldValue, workItemTypes, disabled, setWorkItemType, labels }) => {
    return (
        <Form.Group className="left-side">
            <Form.Label>{labels?.TaskSingular ?? 'Task'} Type <span className='red'>*</span></Form.Label>
            <Form.Control
                id='workItemType'
                name="workItemType"
                className='tasktypeOptionsContainer'
                value={values.workItemType}
                onChange={(e) => {
                    handleChange(e);
                    const newWorkItemType = parseInt(e.target.value);
                    const currTaskType = workItemTypes.find(type => type.Id === newWorkItemType);
                    if (!values.endDateChange) setEndDate(setFieldValue, currTaskType);
                    setWorkItemType(currTaskType);
                }}
                as="select"
                isInvalid={touched.workItemType && errors.workItemType}
                disabled={disabled}
            >
                <option value="0">-Select-</option>
                {workItemTypes.map((type, index) => <option key={`${type.Id}_${type.Type}`} value={type.Id}>{type.Type}</option>)}
            </Form.Control>
            <Form.Control.Feedback type="invalid">{errors.workItemType}</Form.Control.Feedback>
        </Form.Group>
    )
};

export const Priority = ({ values, touched, errors, handleChange, disabled }) => {
    return (
        <Form.Group className="right-side">
            <Form.Label>Priority <span className='red'>*</span></Form.Label>
            <Form.Control
                name="PriorityId"
                value={values.PriorityId}
                onChange={(e) => { handleChange(e) }}
                as="select"
                isInvalid={touched.PriorityId && errors.PriorityId}
                disabled={disabled}
            >
                <option value="0">-Select-</option>
                <option value="1">Low</option>
                <option value="2">Medium</option>
                <option value="3">High</option>
                <option value="4">Urgent</option>
            </Form.Control>
            <Form.Control.Feedback type="invalid">{errors.PriorityId}</Form.Control.Feedback>
            {(!errors.PriorityId && errors.workItemType) && <div className='taskTypeErrorFix'></div>}
        </Form.Group>
    );
}

export const StartDate = ({ values, errors, setFieldValue, dateFormatID, disabled }) => {
    return (
        <Form.Group className="left-side">
            <Form.Label>Start Date <span className='red'>*</span></Form.Label>
            <FluentDatePicker
                id="Start"
                autoComplete="off"
                name="Start"
                placeholder="Click to select a start date"
                value={values.Start}
                onChange={(value) => { setFieldValue("Start", value); }}
                isInvalid={errors.Start !== undefined}
                feedback={errors.Start}
                dateFormatID={dateFormatID}
                minDate={values.ProjectId !== 0 ? getDateObj(values, true) : new Date("December 31, 100 03:24:00")}
                maxDate={typeof (values.Due) != "undefined" ? values.Due : new Date()}
                disabled={disabled}
            />
        </Form.Group>
    )
}

export const EndDate = ({ values, errors, setFieldValue, dateFormatID, disabled }) => {
    return (
        <Form.Group className="right-side">
            <Form.Label>Due Date <span className='red'>*</span></Form.Label>
            <FluentDatePicker
                id="Due"
                autoComplete="off"
                name="Due"
                placeholder="Click to select a end date"
                value={values.Due}
                onChange={(value) => {
                    setFieldValue("Due", value);
                    if (!values.endDateChange) {
                        setFieldValue("endDateChange", true, false);
                    }
                }}
                minDate={typeof (values.Start) != "undefined" ? values.Start : new Date()}
                maxDate={values.ProjectId !== 0 ? getDateObj(values, false) : new Date("December 31, 3001 03:24:00")}
                isInvalid={errors.Due !== undefined}
                feedback={errors.Due}
                dateFormatID={dateFormatID}
                disabled={disabled}
            />
        </Form.Group>
    )
}

export const TaskDetailsSection = ({ values, touched, errors, handleChange, setFieldValue, workItemTypes, activeProjects, dateFormatID, setTaskOtherMembers, disabled, borderBottom, setWorkItemType, setTaskName, labels, onProjectSelected }) => {
    return (
        <div className={`section-container ${borderBottom ? 'border-bottom':''}`}>
            <div className="left-panel">
                <ProjectName
                    values={values}
                    touched={touched}
                    errors={errors}
                    handleChange={handleChange}
                    activeProjects={activeProjects}
                    dateFormatID={dateFormatID}
                    setTaskOtherMembers={(setTaskOtherMembers)}
                    setFieldValue={setFieldValue}
                    disabled={disabled}
                    labels={labels}
                    onProjectSelected={onProjectSelected}
                />
                <TaskName
                    values={values}
                    touched={touched}
                    errors={errors}
                    handleChange={handleChange}
                    disabled={disabled}
                    setTaskName={setTaskName}
                    labels={labels}
                />
            </div>
            <div className="right-panel">
                <div className="task-priority">
                    <TaskType
                        values={values}
                        touched={touched}
                        errors={errors}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        workItemTypes={workItemTypes}
                        disabled={disabled}
                        setWorkItemType={setWorkItemType}
                        labels={labels}
                    />

                    <Priority
                        values={values}
                        touched={touched}
                        errors={errors}
                        handleChange={handleChange}
                        disabled={disabled}
                    />
                </div>
                <div className="dates">
                    <StartDate
                        values={values}
                        touched={touched}
                        errors={errors}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        dateFormatID={dateFormatID}
                        disabled={disabled}
                    />
                    <EndDate
                        values={values}
                        touched={touched}
                        errors={errors}
                        handleChange={handleChange}
                        setFieldValue={setFieldValue}
                        dateFormatID={dateFormatID}
                        disabled={disabled}
                    />
                </div>
            </div>
        </div>

    );
};

TaskDetailsSection.propTypes = {
    values: PropTypes.shape({
        ProjectId: PropTypes.number.isRequired,
        Name: PropTypes.string.isRequired,
        workItemType: PropTypes.number.isRequired,
        PriorityId: PropTypes.number.isRequired,
        Start: PropTypes.instanceOf(Date).isRequired,
        Due: PropTypes.instanceOf(Date).isRequired,
        allApprovers: PropTypes.bool,
        endDateChange: PropTypes.bool,
        selectedProject: PropTypes.object,
    }).isRequired,
    touched: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    workItemTypes: PropTypes.array.isRequired,
    activeProjects: PropTypes.array.isRequired,
    dateFormatID: PropTypes.string.isRequired,
    setTaskOtherMembers: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    borderBottom: PropTypes.bool,
    setWorkItemType: PropTypes.func.isRequired,
    setTaskName: PropTypes.func.isRequired,
    labels: PropTypes.shape({
        ProjectSingular: PropTypes.string,
        ProjectPlural: PropTypes.string,
        ProjectSingularPossessive: PropTypes.string,
        ProjectPluralPossessive: PropTypes.string,
        TaskSingular: PropTypes.string,
        TaskPlural: PropTypes.string,
        TaskSingularPossessive: PropTypes.string,
        TaskPluralPossessive: PropTypes.string,
        FormSingular: PropTypes.string,
        FormPlural: PropTypes.string,
        FormSingularPossessive: PropTypes.string,
        FormPluralPossessive: PropTypes.string,
    }),
    onProjectSelected: PropTypes.func,
};

TaskName.propTypes = {
    values: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    labels: PropTypes.shape({
        ProjectSingular: PropTypes.string,
        ProjectPlural: PropTypes.string,
        ProjectSingularPossessive: PropTypes.string,
        ProjectPluralPossessive: PropTypes.string,
        TaskSingular: PropTypes.string,
        TaskPlural: PropTypes.string,
        TaskSingularPossessive: PropTypes.string,
        TaskPluralPossessive: PropTypes.string,
        FormSingular: PropTypes.string,
        FormPlural: PropTypes.string,
        FormSingularPossessive: PropTypes.string,
        FormPluralPossessive: PropTypes.string,
    }),
};

ProjectName.propTypes = {
    values: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    activeProjects: PropTypes.array.isRequired,
    dateFormatID: PropTypes.string.isRequired,
    setTaskOtherMembers: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    onProjectSelected: PropTypes.func,
    labels: PropTypes.shape({
        ProjectSingular: PropTypes.string,
        ProjectPlural: PropTypes.string,
        ProjectSingularPossessive: PropTypes.string,
        ProjectPluralPossessive: PropTypes.string,
        TaskSingular: PropTypes.string,
        TaskPlural: PropTypes.string,
        TaskSingularPossessive: PropTypes.string,
        TaskPluralPossessive: PropTypes.string,
        FormSingular: PropTypes.string,
        FormPlural: PropTypes.string,
        FormSingularPossessive: PropTypes.string,
        FormPluralPossessive: PropTypes.string,
    }),
};

TaskType.propTypes = {
    values: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    workItemTypes: PropTypes.array.isRequired,
    labels: PropTypes.shape({
        ProjectSingular: PropTypes.string,
        ProjectPlural: PropTypes.string,
        ProjectSingularPossessive: PropTypes.string,
        ProjectPluralPossessive: PropTypes.string,
        TaskSingular: PropTypes.string,
        TaskPlural: PropTypes.string,
        TaskSingularPossessive: PropTypes.string,
        TaskPluralPossessive: PropTypes.string,
        FormSingular: PropTypes.string,
        FormPlural: PropTypes.string,
        FormSingularPossessive: PropTypes.string,
        FormPluralPossessive: PropTypes.string,
    }),
};

Priority.propTypes = {
    values: PropTypes.object.isRequired,
    touched: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
};

StartDate.propTypes = {
    values: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    dateFormatID: PropTypes.string.isRequired,
};

EndDate.propTypes = {
    values: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    dateFormatID: PropTypes.string.isRequired,
};

export default TaskDetailsSection;